// src/hooks/usePermission.js
/**
 * Хук для проверки прав доступа текущего пользователя.
 *
 * Права хранятся в localStorage['permissions'] как JSON.
 * Обновляются при каждом логине через /auth/me.
 *
 * Иерархия:
 *   is_super_admin → canView=true, canEdit=true для всего
 *   is_admin       → canView=true, canEdit=true для всего
 *   остальные      → смотрим permissions[module]
 *
 * Уровни: none < view < edit
 */

const LEVEL_ORDER = { none: 0, view: 1, edit: 2 };

/**
 * Получает права из localStorage.
 * @returns {{ [module: string]: 'none'|'view'|'edit' }}
 */
export function getStoredPermissions() {
  try {
    const raw = localStorage.getItem('permissions');
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

/**
 * Сохраняет права в localStorage.
 * @param {{ [module: string]: string }} permissions
 */
export function storePermissions(permissions) {
  if (permissions && typeof permissions === 'object') {
    localStorage.setItem('permissions', JSON.stringify(permissions));
  }
}

/**
 * Хук проверки прав для конкретного модуля.
 *
 * @param {string} module - название модуля (equipment, employees, ...)
 * @returns {{ canView: boolean, canEdit: boolean, level: string }}
 *
 * @example
 * const { canView, canEdit } = usePermission('equipment');
 * if (!canView) return <Navigate to="/no-access" />;
 */
export function usePermission(module) {
  const isAdmin      = localStorage.getItem('is_admin')       === 'true';
  const isSuperAdmin = localStorage.getItem('is_super_admin') === 'true';

  // Администраторы имеют полный доступ
  if (isSuperAdmin || isAdmin) {
    return { canView: true, canEdit: true, level: 'edit' };
  }

  const permissions = getStoredPermissions();
  const level = permissions[module] || 'none';
  const idx   = LEVEL_ORDER[level] ?? 0;

  return {
    canView: idx >= LEVEL_ORDER.view,
    canEdit: idx >= LEVEL_ORDER.edit,
    level,
  };
}

/**
 * Проверяет есть ли доступ хотя бы к одному из перечисленных модулей.
 * Используется для отображения групп в меню.
 *
 * @param {string[]} modules
 * @param {'view'|'edit'} minLevel
 */
export function useAnyPermission(modules, minLevel = 'view') {
  const isAdmin      = localStorage.getItem('is_admin')       === 'true';
  const isSuperAdmin = localStorage.getItem('is_super_admin') === 'true';

  if (isSuperAdmin || isAdmin) return true;

  const permissions = getStoredPermissions();
  const required = LEVEL_ORDER[minLevel] ?? 1;

  return modules.some(m => (LEVEL_ORDER[permissions[m]] ?? 0) >= required);
}

/**
 * Возвращает список модулей к которым у пользователя есть доступ.
 * @param {'view'|'edit'} minLevel
 * @returns {string[]}
 */
export function getAccessibleModules(minLevel = 'view') {
  const isAdmin      = localStorage.getItem('is_admin')       === 'true';
  const isSuperAdmin = localStorage.getItem('is_super_admin') === 'true';

  const ALL_MODULES = [
    'equipment', 'employees', 'services', 'consumables',
    'acts', 'return_acts', 'it_analytics', 'admin',
  ];

  if (isSuperAdmin || isAdmin) return ALL_MODULES;

  const permissions = getStoredPermissions();
  const required = LEVEL_ORDER[minLevel] ?? 1;
  return ALL_MODULES.filter(m => (LEVEL_ORDER[permissions[m]] ?? 0) >= required);
}

export default usePermission;